import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useProject } from '../api/projects';
import {
  COLUMN_ORDER,
  groupByStatus,
  useProjectTasks,
} from '../api/tasks';
import { STATUS_LABELS } from '../lib/types';
import { useWebSocket } from '../api/ws';
import { useQueryClient } from '@tanstack/react-query';
import TaskCard from '../components/TaskCard';
import NewTaskForm from '../components/NewTaskForm';

export default function Board() {
  const params = useParams<{ slug: string }>();
  const slug = params.slug!;
  const { data: project } = useProject(slug);
  const { data: tasks, isLoading } = useProjectTasks(slug);
  const [showNew, setShowNew] = useState(false);
  const qc = useQueryClient();

  // Live updates: when ANY task in this project changes, refresh the list.
  useWebSocket(`/ws/projects/${slug}`, (msg) => {
    if (msg.kind === 'task_mutation' || msg.kind === 'task_event') {
      qc.invalidateQueries({ queryKey: ['project-tasks', slug] });
    }
  });

  if (!project) return <div className="text-slate-500">Loading project…</div>;

  const grouped = tasks ? groupByStatus(tasks) : null;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">{project.name}</h1>
          <div className="text-xs text-slate-500 font-mono">{project.local_path}</div>
        </div>
        <button
          onClick={() => setShowNew(true)}
          className="px-3 py-1.5 bg-emerald-600 text-white text-sm rounded hover:bg-emerald-700"
        >
          + New Task
        </button>
      </div>

      {isLoading && <div className="text-slate-500">Loading tasks…</div>}

      {grouped && (
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {COLUMN_ORDER.map((status) => (
            <div key={status} className="flex flex-col min-w-0">
              <div className="flex items-center justify-between mb-2 px-1">
                <span className="text-sm font-medium text-slate-700">
                  {STATUS_LABELS[status]}
                </span>
                <span className="text-xs text-slate-400">
                  {grouped[status].length}
                </span>
              </div>
              <div className="flex-1 space-y-2 min-h-[200px] bg-slate-100 rounded p-2 scrollbar-thin overflow-y-auto max-h-[70vh]">
                {grouped[status].length === 0 && (
                  <div className="text-xs text-slate-400 text-center py-4">empty</div>
                )}
                {grouped[status].map((t) => (
                  <TaskCard key={t.id} task={t} projectSlug={slug} project={project} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {showNew && (
        <NewTaskForm
          projectSlug={slug}
          onClose={() => setShowNew(false)}
        />
      )}
    </div>
  );
}
